import { ZoneCalcMethod } from '@trainme/db/client';
import type { PlanGoal } from '@trainme/db/client';
import type { TrainingSettingInput, SportsSpeeds } from './types';
import sportsSpeeds from './data/defaultSportsSpeeds.json';

export const zoneColors = {
  'zone-1': 'rgb(42, 97, 124)',
  'zone-2': 'rgb(130, 202, 157)',
  'zone-3': 'rgb(255, 198, 88)',
  'zone-4': 'rgb(255, 115, 0)',
  'zone-5': 'rgb(231, 33, 33)',
  'zone-6': 'rgb(194, 47, 197)',
  highlight: 'rgb(253, 230, 138)',
} as const;

export type GoalNode = {
  label: string;
  /** Present on selectable leaves. */
  goal?: PlanGoal;
  /** Fixed distance a leaf implies (e.g. Ironman); otherwise the goal default applies. */
  distanceKm?: number;
  children?: GoalNode[];
};

export const GOAL_TREE: GoalNode[] = [
  {
    label: 'Running',
    children: [
      { label: 'Marathon', goal: 'Marathon' },
      { label: 'Half Marathon', goal: 'HalfMarathon' },
      { label: '10K Run', goal: 'TenKRun' },
      { label: '5K Run', goal: 'FiveKRun' },
    ],
  },
  {
    label: 'Triathlon',
    children: [
      { label: 'Ironman (140.6)', goal: 'Triathlon', distanceKm: 226 },
      { label: 'Half Ironman (70.3)', goal: 'Triathlon', distanceKm: 113 },
    ],
  },
  { label: 'Swimming', goal: 'Swimming' },
  { label: 'Happy Run', goal: 'HappyRun' },
];

/** Flat goal list derived from GOAL_TREE; a group of one goal takes the group's label. */
export const PLAN_GOALS: Array<{ value: PlanGoal; label: string }> =
  GOAL_TREE.flatMap((node) => {
    if (!node.children) return [{ value: node.goal!, label: node.label }];
    const goals = [...new Set(node.children.map((c) => c.goal!))];
    return goals.length === 1
      ? [{ value: goals[0], label: node.label }]
      : node.children.map((c) => ({ value: c.goal!, label: c.label }));
  });

/** Label of the top-level tree group a goal belongs to (e.g. Marathon -> Running). */
export const getGoalGroup = (goal: PlanGoal | null | undefined) =>
  goal
    ? GOAL_TREE.find(
        (n) => n.goal === goal || n.children?.some((c) => c.goal === goal)
      )?.label
    : undefined;

export const DEFAULT_DISTANCE_BY_GOAL: Record<PlanGoal, number> = {
  Marathon: 42.2,
  HalfMarathon: 21.1,
  Triathlon: 51.5,
  Swimming: 2,
  TenKRun: 10,
  FiveKRun: 5,
  HappyRun: 5,
};

const DEFAULT_ZONE_CALC_METHOD: ZoneCalcMethod = ZoneCalcMethod.Zoladz;

export const DEFAULT_SPORT_SPEEDS: SportsSpeeds = sportsSpeeds as SportsSpeeds;

export const defaultSettings: TrainingSettingInput = {
  maxHeartRate: 0,
  restHeartRate: 0,
  ftp: 0,
  createdAt: new Date(),
  updatedAt: new Date(),
  sportsTypes: [],
  sportsSpeeds: DEFAULT_SPORT_SPEEDS,
  zoneCalcMethod: DEFAULT_ZONE_CALC_METHOD,
  unitSystem: 'scientific',
  user: {
    connect: {
      id: '',
    },
  },
};

export const workoutStepsExampleLines = [
  '5min Z2 HR Warm up',
  '20min Z3 HR active',
  '5min Z1 HR cool down',
];

export const workoutStepsInputPlaceholder = `
Example steps:

-----------------------------
${workoutStepsExampleLines.join('\n')}
-----------------------------

Each line is a step. '5min' means 5 minutes, 'Z1' means zone one.`;

export { WorkoutStatus } from './types';
